import React, { useState } from 'react';
import './App.css';
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  List,
  ListItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useDispatch } from 'react-redux';
import { AppDispatch } from './app/store';
import { useActions, useAppSelector } from './app/hooks';
import { getGeneralSlice, getProviders, getToken } from './app/store/general/general.slice';
import { red } from '@mui/material/colors';

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { query: queryData, token: tokenData } = useAppSelector(getGeneralSlice);

  const { status: queryStatus, error: queryError, providers, totalPages, page, requestTime } = queryData;
  const { status: tokenStatus, token, error: tokenError } = tokenData;

  const [queryString, setQueryString] = useState<string>('');

  const { setQuery, setPage } = useActions();

  const onSearchBtnClick = () => {
    // Update query string
    dispatch(setQuery(queryString));

    // Query token first, if it does not exist
    dispatch(getProviders(false))
      .unwrap()
      .then(() => {
        console.log('succ');
      })
      .catch((e) => {
        console.log('err', e);
      });
  };

  const onPaginationChange = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(setPage(value));

    // Query token first, if it does not exist
    dispatch(getProviders(true))
      .unwrap()
      .then(() => {
        console.log('succ');
      })
      .catch((e) => {
        console.log('err', e);
      });
  };

  return (
    <Box
      className={'app'}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: '375px',
        minHeight: '100vh',
        bgcolor: 'primary.main',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '2rem 0',
      }}>
      <Container
        sx={{
          background: '#f2f2f2',
          minHeight: '40rem',
          padding: '2rem',
        }}
        maxWidth='lg'>
        {/* Loader element start */}
        {(queryStatus === 'loading' || tokenStatus === 'loading') && (
          <Backdrop
            sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
            open={true}>
            <CircularProgress color='inherit' />
          </Backdrop>
        )}
        {/* Loader element end */}

        {/* Grid Start */}
        <Grid
          container
          spacing={2}>
          {/* Search Field Block Start */}
          <Grid size={12}>
            {tokenError && (
              <Alert
                sx={{ mb: '2rem' }}
                severity={'error'}>
                {`Token Error: ${tokenError}`}
              </Alert>
            )}

            {queryError && (
              <Alert
                sx={{ mb: '2rem' }}
                severity={'error'}>
                {`Query Error: ${queryError}`}
              </Alert>
            )}

            {/* Search Field Start */}
            <TextField
              id='outlined-multiline-static'
              label='Search'
              multiline
              rows={4}
              sx={{
                width: '100%',
                backgroundColor: '#ffffff',
              }}
              placeholder='Search...'
              onChange={(event) => setQueryString(event.target.value)}
            />
            {/* Search Field End */}

            <Stack
              direction='row'
              spacing={2}
              sx={{
                margin: '1rem 0',
              }}>
              {/* Search Button Start */}
              <Button
                variant='contained'
                onClick={onSearchBtnClick}>
                Search
              </Button>
              {/* Search Button End */}

              {/* Request Time */}
              {queryStatus === 'succeeded' && requestTime && (
                <Alert severity={'success'}>{`In ${requestTime.toFixed(3)} seconds`}</Alert>
              )}
              {/* Request Time */}
            </Stack>
          </Grid>
          {/* Search Field Block End */}

          {/* Providers List Start */}
          {providers.length > 0 && (
            <Grid size={12}>
              <List
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'stretch',
                  gap: 0,
                }}>
                {providers.map((provider) => (
                  <ListItem sx={{ width: '50%', padding: '1rem' }}>
                    <Card sx={{ width: '100%', height: '100%' }}>
                      <CardHeader
                        avatar={
                          <Avatar
                            sx={{ bgcolor: red[500] }}
                            aria-label='doctor'>
                            {provider.Document.FirstName.charAt(0)}
                          </Avatar>
                        }
                        title={`${provider.Document.FirstName} ${provider.Document.LastName}`}
                        subheader={provider.Document.Degree}
                      />
                      <CardContent>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.secondary' }}>
                          {`Gender: ${provider.Document.Gender}`}
                          <br />
                          {`Specialty: ${provider.Document.Specialty}`}
                          <br />
                          {`Address : ${provider.Document.ZIP}, ${provider.Document.State}, ${provider.Document.City},  ${provider.Document.Address1} ${provider.Document.Address2 ?? ''}`}
                          <br />
                          {`Phone : ${provider.Document.Phone}`}
                          <br />
                          {provider.Document.Languages && (
                            <>
                              {`Language(s): ${provider.Document.Languages}`}
                              <br />
                            </>
                          )}
                        </Typography>
                      </CardContent>
                    </Card>
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}
          {/* Providers List End */}

          {/* Pagination Start */}
          {providers.length > 0 && (
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPaginationChange}
              sx={{
                width: '100%',
                justifyContent: 'center',
                '& ul': {
                  justifyContent: 'center',
                },
              }}
              page={page}
            />
          )}
          {/* Pagination End */}

          {/* Empty results message */}
          {queryStatus === 'succeeded' && providers.length < 1 && (
            <Alert
              sx={{ mb: '2rem' }}
              severity={'info'}>
              {`Nothing found for your current query. Try modifying it, please.`}
            </Alert>
          )}
          {/* Empty results message */}
        </Grid>
        {/* Grid End */}
      </Container>
    </Box>
  );
}

export default App;
